import { z } from "zod";
import { getPlainTextFromDoc } from "../../tiptap/plaintext";
import { DiscussionComment, DiscussionListEntry, DiscussionParticipant } from "../discussion";
import { FileId } from "../file";
import { IsoDateString } from "../isoDate";
import { TipTapDocSchema } from "../tiptapDoc";

const MessageInput = z.object({
    contentJson: TipTapDocSchema,
    attachments: z.array(FileId).optional(),
});

// PRTL-1348: a participant is an (org + user) combo — the org is the context
// the user is added under (Compass staff resolve to the Compass org
// server-side). Sent alongside the legacy flat id arrays during the
// transition; the server prefers `participants` when present and infers orgs
// for legacy payloads. The flat arrays are dropped with PRTL-1357.
export const ParticipantInput = z.object({
    userId: z.number().int().positive(),
    organizationId: z.number().int().positive(),
});
export type ParticipantInput = z.infer<typeof ParticipantInput>;

export const schema = {
    create: {
        input: z.object({
            title: z
                .string()
                .trim()
                .min(1, "Title must be at least 1 character long")
                .max(100, "Title must be at most 100 characters long"),
            users: z.array(z.number().int().positive()).default([]),
            participants: z.array(ParticipantInput).max(50).optional(),
            message: MessageInput.refine(
                (val) =>
                    getPlainTextFromDoc(val.contentJson).trim().length > 0 ||
                    (val.attachments && val.attachments.length > 0),
                {
                    message: "Message must have text or at least one attachment.",
                    path: ["contentJson"],
                },
            ),
        }),
        output: z.object({
            id: z.number(),
        }),
    },
    get: {
        input: z.object({ id: z.number() }),
        output: DiscussionListEntry.extend({
            createdAt: IsoDateString,
            comments: z.array(DiscussionComment),
        }).nullish(),
    },
    getPageForId: {
        input: z.object({ id: z.number(), pageSize: z.number().default(10) }),
        output: z.object({ page: z.number().optional() }),
    },
    latest: {
        input: z
            .object({
                limit: z.number().lte(10).gte(1).optional().default(10),
            })
            .default({
                limit: 10,
            }),
        output: z.array(
            z.object({
                id: z.number(),
                title: z.string(),
                users: z.array(DiscussionParticipant).nonempty(),
            }),
        ),
    },
    list: {
        input: z.object({
            search: z.coerce.string().min(0).max(100).optional(),
            page: z.coerce.number().int().min(0).optional(),
            pageSize: z.coerce.number().int().min(1).max(30).optional(),
            organizationId: z.coerce.number().int().optional(),
            documentId: z.string().optional(),
            id: z.coerce.number().optional(),
        }),
        output: z.object({
            discussions: z.array(DiscussionListEntry),
            totalFound: z.number(),
            total: z.number(),
        }),
    },
    reply: {
        input: z.object({
            id: z.number(),
            authorId: z.number().optional(),
            message: MessageInput,
        }),
        output: z.void(),
    },
    updateComment: {
        input: z.object({
            commentId: z.number(),
            contentJson: TipTapDocSchema,
            attachments: z.array(FileId).optional(),
        }),
        output: z.void(),
    },

    updateTitle: {
        input: z.object({
            discussionId: z.number(),
            title: z
                .string()
                .trim()
                .min(1, "Title must be at least 1 character long")
                .max(100, "Title must be at most 100 characters long"),
        }),
        output: z.void(),
    },

    addParticipants: {
        input: z.object({
            discussionId: z.number().int().positive(),
            userIds: z.array(z.number().int().positive()).min(1).max(50),
            participants: z.array(ParticipantInput).max(50).optional(),
        }),
        output: z.object({ addedUserIds: z.array(z.number()) }),
    },

    removeParticipants: {
        input: z.object({
            discussionId: z.number().int().positive(),
            userIds: z.array(z.number().int().positive()).min(1).max(50),
        }),
        output: z.object({ removedUserIds: z.array(z.number()) }),
    },

    delete: {
        input: z.object({ discussionId: z.number().int().positive() }),
        output: z.object({ success: z.boolean() }),
    },
};
